// @flow
import type { ReceiverStatus } from 'Common/dvr/types';
import type { Action, State, Viewer, Receiver } from './types';
import { hasSideloading } from '../utilities/receiver';
import { normalizeReceiversList } from './utils';

const initialState: State = {
  formDisabled: false,
  error: undefined,
  viewer: undefined,
  signInType: 'none',
  isFromPairing: false,
  androidSsoAccountOwner: undefined,
  redirectToLogout: false,
  isTvProviderAuthenticating: false,
  isIosMandyLoggingIn: false,
  radishSession: {
    rs: undefined,
    created: undefined,
    expires: undefined,
  },
};

export const LoggingInAuxiliaryMessage = {
  '0': '',
  '1': 'This is taking a little longer than expected...',
  '2': 'Almost there...',
};

// Shows if we have passed Synacor/Pairing auth.
export const isAuthorized = (viewer?: Viewer) => {
  const { uuid, samlToken } = viewer || {};
  return !!uuid && !!samlToken;
};

// Shows if we have obtained a session from Radish.
export const isSignedIn = (viewer?: Viewer) => {
  const { euid } = viewer || {};
  return !!euid;
};

const hasSideloadingReceiver = (receivers: Receiver[]) =>
  receivers
    ? receivers.reduce((prev, receiver) => prev || hasSideloading(receiver.model), false)
    : false;

const reducer = (state: State = initialState, action: Action): State => {
  switch (action.type) {
    case 'ON_AUTH': {
      const viewer = { ...state.viewer, ...action.payload.accountInfo };
      const { isFromPairing, androidSsoAccountOwner } = action.payload;
      return {
        ...state,
        viewer,
        isFromPairing,
        androidSsoAccountOwner,
      };
    }
    case 'ON_IOS_NATIVE_LOGIN': {
      return {
        ...state,
        isTvProviderAuthenticating:
          action.loginType === 'tvProvider' ? action.isLoggingIn : state.isTvProviderAuthenticating,
        isIosMandyLoggingIn:
          action.loginType === 'mandy' ? action.isLoggingIn : state.isIosMandyLoggingIn,
      };
    }
    case 'ON_AUTH_ERROR': {
      return state;
    }
    case 'SIGN_OUT': {
      return { ...initialState, signInRetryCount: action.signInRetryCount };
    }
    case 'SIGN_IN_DONE': {
      const viewer = {
        ...state.viewer,
        ...action.payload.user,
        // DO NOT REMOVE. Radish sometimes passes INVALID Enterprise token. Keep the one from Synacor
        enterprise: state.viewer?.enterprise || action.payload?.user?.enterprise,
        hasSideloadingReceiver: hasSideloadingReceiver(action.payload.user.receivers),
        samlToken: undefined, // Do not keep a synacor token once radish session is obtained,
      };
      viewer.receivers = normalizeReceiversList(viewer.receivers);
      return {
        ...state,
        formDisabled: false,
        viewer,
        signInType: 'online',
        redirectToLogout: false,
        signInRetryCount: undefined,
      };
    }
    case 'SIGN_IN_DONE_CACHED':
      return {
        ...state,
        signInType: 'offline',
      };
    case 'CREATE_RADISH_SESSION': {
      const { rs, expires, created } = action.payload;
      return {
        ...state,
        radishSession: {
          rs,
          expires,
          created: created || Date.now(),
        },
      };
    }
    case 'RECEIVE_RECEIVERS_LIST': {
      let {
        payload: { receivers },
      } = action;

      const {
        payload: { joeys },
      } = action;

      if (!state.viewer) {
        return state;
      }
      receivers = normalizeReceiversList(receivers);

      return {
        ...state,
        viewer: { ...state.viewer, receivers, joeys },
      };
    }
    case 'REQUEST_RECEIVER_REBOOT': {
      const receivers = state.viewer?.receivers;
      if (!receivers) {
        return state;
      }

      const {
        payload: { receiverId },
      } = action;

      return {
        ...state,
        viewer: {
          ...state.viewer,
          receivers: receivers.map((receiver: Receiver) =>
            receiver.receiver_id === receiverId ? { ...receiver, state: 'reboot' } : receiver,
          ),
        },
      };
    }
    case 'RECEIVE_RECEIVER_REBOOT':
      return state;
    case 'REQUEST_RECEIVER_REBOOT_ERROR':
      return state;
    // $FlowExpectedError
    case 'RECEIVE_RECEIVERS_STATUS': {
      const updatedReceivers: Array<ReceiverStatus> = action.payload;
      const { viewer: { receivers = [] } = {} } = state;

      const receiversWithUpdatedStatus = receivers.map(receiver => {
        const updatedStatus = updatedReceivers.find(r => r.receiver_id === receiver.receiver_id);
        return updatedStatus ? { ...receiver, state: updatedStatus.status } : receiver;
      });

      return {
        ...state,
        viewer: {
          ...state.viewer,
          receivers: receiversWithUpdatedStatus,
        },
      };
    }
    case 'REDIRECT_TO_LOGOUT': {
      return {
        ...state,
        redirectToLogout: true,
      };
    }
    default:
      /* Following line assures switch exhaustiveness */
      (action.type: empty); /* eslint-disable-line */
      return state;
  }
};

export default reducer;
